<script lang="ts" setup>
import { onMounted, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { usePermissionsStore } from '@/stores'
import type { AccountRole, Delegation } from '@/types/rbac'

const { d } = useI18n()
const router = useRouter()
const permissionsStore = usePermissionsStore()

const loading = ref(true)
const accountRoles = ref<AccountRole[]>([])

const delegations = computed(() => permissionsStore.delegations)

const delegationsByDelegator = computed(() => {
  const grouped = new Map<string, Delegation[]>()
  for (const delegation of delegations.value) {
    const key = delegation.delegator_email
    if (!grouped.has(key)) {
      grouped.set(key, [])
    }
    grouped.get(key)!.push(delegation)
  }
  return grouped
})

const formatDate = (dateStr: string): string => {
  try {
    return d(new Date(dateStr), 'short')
  } catch {
    return dateStr
  }
}

const formatPermission = (perm: string): string => {
  const permLabels: Record<string, string> = {
    'dns:read': 'DNS lesen',
    'dns:write': 'DNS schreiben',
    'dyndns:read': 'DynDNS lesen',
    'dyndns:write': 'DynDNS schreiben',
    'mailbox:read': 'Mailboxen lesen',
    'mailbox:write': 'Mailboxen verwalten',
    'mailbox:delete': 'Mailboxen löschen',
    'mailinglist:read': 'Mailinglisten lesen',
    'mailinglist:write': 'Mailinglisten verwalten'
  }
  return permLabels[perm] || perm
}

const goToDomain = (domainId: number) => {
  router.push({ name: 'dashboardDomainVerwaltung', params: { id: domainId } })
}

onMounted(async () => {
  loading.value = true
  try {
    accountRoles.value = await permissionsStore.fetchAccountRoles()
    await permissionsStore.fetchDelegations()
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <div class="roles-page">
    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate color="primary" size="32" />
      <span>{{ $t('dashboard.account.roles.loading') }}</span>
    </div>

    <template v-else>
      <!-- Your Roles Section -->
      <section class="section">
        <div class="section-header">
          <div class="section-icon">
            <v-icon icon="mdi-shield-account" size="20" />
          </div>
          <div class="section-title">
            <h3>{{ $t('dashboard.account.roles.yourRoles') }}</h3>
          </div>
        </div>

        <div v-if="accountRoles.length === 0" class="empty-state">
          <v-icon icon="mdi-shield-off-outline" size="32" />
          <span class="empty-title">{{ $t('dashboard.account.roles.noRoles') }}</span>
          <span class="empty-description">{{ $t('dashboard.account.roles.noRolesDescription') }}</span>
        </div>

        <div v-else class="roles-list">
          <div v-for="role in accountRoles" :key="role.role_id" class="role-card">
            <div class="role-icon">
              <v-icon icon="mdi-badge-account" size="24" />
            </div>
            <div class="role-content">
              <div class="role-name">{{ role.display_name }}</div>
              <div v-if="role.description" class="role-description">{{ role.description }}</div>
              <div class="role-meta">
                <span class="meta-item">
                  <v-icon icon="mdi-calendar-check" size="14" />
                  {{ $t('dashboard.account.roles.grantedAt') }}: {{ formatDate(role.granted_at) }}
                </span>
                <span v-if="role.expires_at" class="meta-item">
                  <v-icon icon="mdi-calendar-clock" size="14" />
                  {{ $t('dashboard.account.roles.expiresAt') }}: {{ formatDate(role.expires_at) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Delegated Access Section -->
      <section class="section">
        <div class="section-header">
          <div class="section-icon delegated">
            <v-icon icon="mdi-account-key" size="20" />
          </div>
          <div class="section-title">
            <h3>{{ $t('dashboard.account.roles.delegatedAccess') }}</h3>
          </div>
        </div>

        <div v-if="delegations.length === 0" class="empty-state">
          <v-icon icon="mdi-key-off" size="32" />
          <span class="empty-title">{{ $t('dashboard.account.roles.noDelegations') }}</span>
          <span class="empty-description">{{ $t('dashboard.account.roles.noDelegationsDescription') }}</span>
        </div>

        <div v-else class="delegations-list">
          <div v-for="[delegatorEmail, domains] in delegationsByDelegator" :key="delegatorEmail" class="delegator-group">
            <div class="delegator-header">
              <v-icon icon="mdi-account-arrow-right" size="16" />
              <span>{{ $t('dashboard.account.roles.delegatedBy') }}: {{ delegatorEmail }}</span>
            </div>
            <div class="delegation-cards">
              <div v-for="delegation in domains" :key="delegation.id" class="delegation-card">
                <div class="delegation-domain">
                  <v-icon icon="mdi-web" size="18" />
                  <span>{{ delegation.domain_name }}</span>
                </div>
                <div class="delegation-permissions">
                  <span
                    v-for="perm in delegation.permissions"
                    :key="perm"
                    class="permission-chip"
                  >
                    {{ formatPermission(perm) }}
                  </span>
                </div>
                <div class="delegation-meta">
                  <span class="meta-item">
                    <v-icon icon="mdi-calendar-check" size="14" />
                    {{ formatDate(delegation.granted_at) }}
                  </span>
                  <span v-if="delegation.expires_at" class="meta-item expires">
                    <v-icon icon="mdi-calendar-clock" size="14" />
                    {{ formatDate(delegation.expires_at) }}
                  </span>
                </div>
                <button class="go-btn" @click="goToDomain(delegation.domain_id)">
                  <v-icon icon="mdi-arrow-right" size="16" />
                  <span>{{ $t('dashboard.account.roles.goToDomain') }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.roles-page {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 16px;
  color: rgba(200, 218, 255, 0.6);
}

.section {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.section-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(0, 176, 255, 0.12);
  border: 1px solid rgba(0, 176, 255, 0.25);
  border-radius: 8px;
  color: #00b0ff;

  &.delegated {
    background: rgba(156, 39, 176, 0.12);
    border-color: rgba(156, 39, 176, 0.25);
    color: #ba68c8;
  }
}

.section-title h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 16px;
  text-align: center;
  color: rgba(200, 218, 255, 0.4);

  .empty-title {
    font-weight: 500;
    color: rgba(200, 218, 255, 0.6);
  }

  .empty-description {
    font-size: 0.85rem;
  }
}

.roles-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.role-card {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: rgba(0, 176, 255, 0.05);
  border: 1px solid rgba(0, 176, 255, 0.15);
  border-radius: 10px;
}

.role-icon {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(0, 176, 255, 0.1);
  border-radius: 8px;
  color: #00b0ff;
  flex-shrink: 0;
  padding-top: 8px;
}

.role-content {
  flex: 1;
  min-width: 0;
}

.role-name {
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 4px;
}

.role-description {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
  margin-bottom: 8px;
}

.role-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.5);

  &.expires {
    color: rgba(255, 183, 77, 0.8);
  }
}

.delegations-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.delegator-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.delegator-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.7);
  padding-left: 4px;
}

.delegation-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.delegation-card {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(156, 39, 176, 0.05);
  border: 1px solid rgba(156, 39, 176, 0.15);
  border-radius: 8px;
}

.delegation-domain {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  min-width: 150px;
}

.delegation-permissions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
}

.permission-chip {
  padding: 2px 8px;
  background: rgba(120, 163, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 4px;
  font-size: 0.7rem;
  color: rgba(200, 218, 255, 0.8);
}

.delegation-meta {
  display: flex;
  gap: 12px;
  font-size: 0.75rem;
}

.go-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: rgba(156, 39, 176, 0.1);
  border: 1px solid rgba(156, 39, 176, 0.25);
  border-radius: 6px;
  color: #ba68c8;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(156, 39, 176, 0.2);
    border-color: rgba(156, 39, 176, 0.4);
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .section {
    background: rgba(0, 0, 0, 0.02);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .section-title h3 {
    color: rgba(0, 0, 0, 0.87);
  }

  .empty-state {
    color: rgba(0, 0, 0, 0.38);

    .empty-title {
      color: rgba(0, 0, 0, 0.6);
    }
  }

  .role-card {
    background: rgba(0, 176, 255, 0.04);
    border-color: rgba(0, 176, 255, 0.12);
  }

  .role-name {
    color: rgba(0, 0, 0, 0.87);
  }

  .role-description {
    color: rgba(0, 0, 0, 0.6);
  }

  .meta-item {
    color: rgba(0, 0, 0, 0.54);
  }

  .delegator-header {
    color: rgba(0, 0, 0, 0.6);
  }

  .delegation-card {
    background: rgba(156, 39, 176, 0.04);
    border-color: rgba(156, 39, 176, 0.12);
  }

  .delegation-domain {
    color: rgba(0, 0, 0, 0.87);
  }

  .permission-chip {
    background: rgba(28, 110, 227, 0.08);
    border-color: rgba(28, 110, 227, 0.15);
    color: rgba(0, 0, 0, 0.7);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .delegation-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .delegation-permissions {
    width: 100%;
  }

  .go-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
